import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useReportScreen } from '../../components/ScreenContext';
import { useIsMirror } from '../../hooks/useIsMirror';
import { nativeWageSheetsApi, type GeneratedWageSheetDto } from '../../api/wageSheets';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';

/**
 * P1e — DUŽ žiniaraščio generavimas: pick the month, generate, review the
 * computed rows. Every number comes from the wage engine (contracts +
 * work-time + frozen rates) — the wizard shows results, P1f adds posting.
 * Mirror workspaces: native-only (ADR-004).
 */
export default function WageSheetWizard() {
  const isMirror = useIsMirror();
  const now = new Date();
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [sheet, setSheet] = useState<GeneratedWageSheetDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      setSheet(await nativeWageSheetsApi.generate(year, month));
    } catch (e: any) {
      setError(e.message || 'Nepavyko sugeneruoti žiniaraščio');
    } finally {
      setLoading(false);
    }
  };

  const [posting, setPosting] = useState(false);
  const [postedId, setPostedId] = useState<number | null>(null);

  const postSheet = async () => {
    if (!sheet) return;
    setPosting(true);
    setError('');
    try {
      const r = await nativeWageSheetsApi.post(sheet.sheetId);
      setPostedId(r.journalEntryId);
    } catch (e: any) {
      setError(e.message || 'Nepavyko užregistruoti');
    } finally {
      setPosting(false);
    }
  };

  const totals = sheet
    ? {
        gross: sheet.items.reduce((s, i) => s + i.grossPay, 0),
        net: sheet.items.reduce((s, i) => s + i.netPayable, 0),
        gpm: sheet.items.reduce((s, i) => s + i.deductedGpm, 0),
        sodra: sheet.items.reduce((s, i) => s + i.contributionSodra, 0),
      }
    : null;

  useReportScreen(
    sheet
      ? `DUŽ žiniaraštis ${sheet.documentNumber}: ${sheet.items.length} eil., bruto ${totals?.gross.toFixed(2)}`
      : 'DUŽ žiniaraščio generavimas',
    { recordCount: sheet?.items.length ?? 0, filters: { metai: String(year), menuo: String(month) } },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">DUŽ žiniaraščiai</h1>
          <p className="text-sm text-gray-500">
            Generuojama iš sutarčių, darbo laiko ir įšaldytų tarifų — registravimas ateina su kita versija.
          </p>
        </div>
        <Link
          to="/payroll"
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
        >
          ← Darbo užmokestis
        </Link>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label>
          Metai:{' '}
          <input type="number" value={year} min={2000} max={2100}
            onChange={e => setYear(Number(e.target.value))} />
        </label>
        <label>
          Mėnuo:{' '}
          <select value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        {!isMirror && (
          <button onClick={generate} disabled={loading}>
            {loading ? <LoadingSpinner /> : sheet ? 'Per sugeneruoti' : 'Generuoti'}
          </button>
        )}
        {!isMirror && sheet && postedId === null && (
          <button onClick={postSheet} disabled={posting || loading}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-40">
            {posting ? '…' : 'Registruoti'}
          </button>
        )}
      </div>

      {isMirror && (
        <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — žiniaraščius skaičiuokite Apskaita5 programoje.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {sheet && (
        <>
          <p className="text-sm text-gray-600">
            Dokumentas <strong>{sheet.documentNumber}</strong>{postedId !== null && ` (užregistruotas, JE ${postedId})`}
            {sheet.isFinal ? ' (galutinis)' : ' (tarpinis)'} · {sheet.items.length} darbuotojai
          </p>
          <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Darbuotojas</th>
                  <th>Sutartis</th>
                  <th>Atlyginimas</th>
                  <th style={{ textAlign: 'right' }}>Bruto</th>
                  <th style={{ textAlign: 'right' }}>GPM</th>
                  <th style={{ textAlign: 'right' }}>PSD</th>
                  <th style={{ textAlign: 'right' }}>VSD</th>
                  <th style={{ textAlign: 'right' }}>Darbd. VSD</th>
                  <th style={{ textAlign: 'right' }}>Avansas</th>
                  <th style={{ textAlign: 'right' }}>Išmokėti</th>
                </tr>
              </thead>
              <tbody>
                {sheet.items.map(i => (
                  <tr key={`${i.workerId}-${i.contractSerial}-${i.contractNumber}`}>
                    <td>{i.workerName}</td>
                    <td>{i.contractSerial} {i.contractNumber} <span className="text-xs text-gray-400">({i.notes})</span></td>
                    <td>{i.contractWage}</td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.grossPay} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.deductedGpm} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.deductedPsd} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.deductedSodra} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.contributionSodra} /></td>
                    <td style={{ textAlign: 'right' }}><MoneyCell value={i.imprestDeducted} /></td>
                    <td style={{ textAlign: 'right' }}><strong><MoneyCell value={i.netPayable} /></strong></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}><strong>Viso</strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={totals!.gross} /></strong></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={totals!.gpm} /></strong></td>
                  <td colSpan={2}></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={totals!.sodra} /></strong></td>
                  <td colSpan={1}></td>
                  <td style={{ textAlign: 'right' }}><strong><MoneyCell value={totals!.net} /></strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
